/**
 * Spelling Regels (SPR) Support Registry
 *
 * Support content (hints, explanations, visuals) for SPR families.
 *
 * Module: SPR (Spelling Regels)
 * Families: 20
 * Coverage: 100% (migrated: 2026-03-25)
 *
 * Created: 2026-03-25
 * Status: ✅ Content migrated from familySupportRegistry.ts (Step 4)
 */

import { FamilySupportEntry } from '../familySupportRegistry';

/**
 * SPR Support Definitions
 *
 * Each entry provides:
 * - defaultExplanation: Student-facing "Zo werkt het" text
 * - hints: Progressive hint steps (title + content)
 * - requiresHints: Whether hints are conceptually needed
 * - requiresVisual: Whether visual aid is conceptually needed
 * - defaultVisual: Optional default visual aid reference
 */
export const SPR_SUPPORT: Record<string, FamilySupportEntry> = {
  'SPR-KLANK-JAGER': {
    familyId: 'SPR-KLANK-JAGER',
    defaultExplanation: 'De jager-regel: in een open lettergreep (ja-ger) schrijf je maar één klinker voor een lange klank. De lettergreep eindigt op een klinker.',
    requiresHints: true,
    requiresVisual: false,
    hints: [{
      title: 'Open lettergreep',
      content: 'Lange klank aan het eind van een klankgroep? Schrijf maar één klinker.\nbo-men (niet boo-men)\nlo-pen (niet loo-pen)\nre-gen (niet ree-gen)'
    }, {
      title: 'Jager vs Bakker — het verschil',
      content: 'Jager-regel (open lettergreep, lange klank):\nja-ger, lo-pen, bo-ten, re-gen → één klinker, één medeklinker\n\nBakker-regel (gesloten lettergreep, korte klank):\nbak-ker, ren-nen, pot-ten, bed-den → één klinker, dubbele medeklinker\n\nTip: luister! Lange klank = jager, korte klank = bakker.'
    }, {
      title: 'Herkennen: luister naar de klank',
      content: 'Stap 1: Spreek het woord uit — klinkt de klinker lang of kort?\nStap 2: Lang? → open lettergreep → jager-regel (één klinker)\nStap 3: Kort? → gesloten lettergreep → bakker-regel (dubbele medeklinker)\n\nVoorbeeld: "boten" — de o klinkt lang → bo-ten (jager)\n"botten" — de o klinkt kort → bot-ten (bakker)'
    }]
  },
  'SPR-KLANK-BAKKER': {
    familyId: 'SPR-KLANK-BAKKER',
    defaultExplanation: 'De bakker-regel: in een gesloten lettergreep (bak-ker) schrijf je een dubbele medeklinker om de korte klank te behouden.',
    requiresHints: true,
    requiresVisual: false,
    hints: [{
      title: 'Regel',
      content: 'Korte klank? Dan dubbele medeklinker in het meervoud.\npot → pot-ten\nbak → bak-ken\nren → ren-nen'
    }, {
      title: 'Waarom verdubbelen?',
      content: 'Zonder dubbele medeklinker wordt de lettergreep open en de klank lang:\nbaken (lang, open) ≠ bakken (kort, gesloten)\nboten (lang, open) ≠ botten (kort, gesloten)\nrenen (lang, open) ≠ rennen (kort, gesloten)\n\nDe dubbele medeklinker "sluit" de lettergreep en houdt de klank kort.'
    }, {
      title: 'Bakker vs Jager — wanneer wat?',
      content: 'Luister naar de klinker:\n• Korte klank (a, e, i, o, u) → bakker-regel → dubbele medeklinker\n  bak-ker, pot-ten, ren-nen, bed-den\n• Lange klank (aa, ee, oo, uu) → jager-regel → enkele medeklinker\n  ja-ger, lo-pen, re-gen, bo-ten\n\nTip: spreek het woord hardop uit!'
    }]
  },
  'SPR-WEET-EI-IJ': {
    familyId: 'SPR-WEET-EI-IJ',
    defaultExplanation: 'Ei of ij? Dit zijn weetwoorden — je moet ze uit je hoofd leren. Tip: de meeste woorden schrijf je met ij. Ei komt vooral voor in: ei, trein, klein, leiden.',
    requiresHints: true,
    requiresVisual: false,
    hints: [{
      title: 'Woorden met ei',
      content: 'Veel voorkomende woorden met ei:\nei, trein, klein, leiden, reis, weit, meisje, eigenlijk, dreigen, peil'
    }, {
      title: 'Tip: de meeste woorden hebben ij',
      content: 'Bij twijfel: schrijf ij. De meeste Nederlandse woorden gebruiken ij.\nVoorbeelden: schrijven, rijden, tijd, wijs, vrij, blij, lijn, fijn\n\nEi-woorden moet je uit je hoofd leren!'
    }]
  },
  'SPR-LEEN-C-S-K': {
    familyId: 'SPR-LEEN-C-S-K',
    defaultExplanation: 'De letter c klinkt als S voor e en i (cent, circus). De c klinkt als K voor a, o en u (cactus, cola). Dit helpt bij het spellen van leenwoorden.',
    requiresHints: true,
    requiresVisual: false,
    hints: [{
      title: 'C als S-klank',
      content: 'C + e/i = S-klank\nVoorbeelden: cent, circus, citroen, cijfer'
    }, {
      title: 'C als K-klank',
      content: 'C + a/o/u = K-klank\nVoorbeelden: cola, cactus, computer, cake'
    }]
  },
  'SPR-TREMA-MV': {
    familyId: 'SPR-TREMA-MV',
    defaultExplanation: 'Een trema (¨) voorkomt dat twee klinkers als één klank worden gelezen. Voorbeeld: zeeën (niet zeeen), ideeën, tweeën.',
    requiresHints: true,
    requiresVisual: false,
    hints: [{
      title: 'Meervoud',
      content: 'Een trema voorkomt dat je twee klinkers als één klank leest.\nideeën (niet ideeen)\nzeeën (niet zeeen)\ntweeën'
    }, {
      title: 'Na ge-, be-, ver-',
      content: 'geëist, geüpload, geëindigd'
    }]
  },
  'SPR-LEEN-FRANS': {
    familyId: 'SPR-LEEN-FRANS',
    defaultExplanation: 'Franse leenwoorden hebben bijzondere klankgroepen: -eau (cadeau, bureau), -oir (trottoir), -ique (boutique). Je moet deze uit je hoofd leren.',
    requiresHints: true,
    requiresVisual: false,
    hints: [{
      title: 'Leenwoorden uit het Frans',
      content: 'chef (niet sjef)\nroute (niet roete)\ntrottoir (niet trotwaar)\nmiljonair (niet miljonar)\ncaissière'
    }]
  },
  'SPR-SAM-TUSSEN-N': {
    familyId: 'SPR-SAM-TUSSEN-N',
    defaultExplanation: 'Bij samenstellingen komt er een tussen-n als het eerste deel een meervoud op -en heeft: pannen+koek = pannenkoek. Uitzondering: als er maar één is (zonnebril).',
    requiresHints: true,
    requiresVisual: false,
    hints: [{
      title: 'Tussen-n',
      content: 'Als het eerste deel een meervoud op -en heeft:\npannen + koek = pannenkoek\npaarden + staart = paardenstaart'
    }, {
      title: 'Uitzondering',
      content: 'Als er maar één is, geen tussen-n:\nzonnebril (er is maar één zon)\nkoninginnedag (maar: koninginnendag mag ook)'
    }]
  },
  'SPR-LEEN-ENGELS': {
    familyId: 'SPR-LEEN-ENGELS',
    defaultExplanation: 'Engelse leenwoorden behouden vaak hun Engelse spelling: computer (niet kompjoeter), team (niet tiem). Let op de klankbeelden.',
    requiresHints: true,
    requiresVisual: false,
    hints: [{
      title: 'Engelse leenwoorden',
      content: 'computer (met c, niet k)\nteam (niet tiem)'
    }]
  },
  'SPR-LEEN-LATIJN': {
    familyId: 'SPR-LEEN-LATIJN',
    defaultExplanation: 'Woorden uit het Latijn en Grieks hebben bijzondere meervouden: museum → musea, centrum → centra, crisis → crises.',
    requiresHints: true,
    requiresVisual: false,
    hints: [{
      title: 'Latijnse meervouden',
      content: 'Woorden uit het Latijn hebben bijzondere meervoudsvormen:\nmuseum → musea\ncentrum → centra\nforum → fora\naquarium → aquaria\nstadion → stadia'
    }]
  },
  'SPR-ACCENT': {
    familyId: 'SPR-ACCENT',
    defaultExplanation: 'Een accent kan de betekenis veranderen: vóór (plaats) vs. voor (ten behoeve van), één (telwoord) vs. een (lidwoord).',
    requiresHints: true,
    requiresVisual: false,
    hints: [{
      title: 'Woorden met é',
      content: 'Sommige woorden schrijf je met é (accent aigu).\nVoorbeelden: café, logé'
    }]
  },
  'SPR-KLANK-VERDUBBEL': {
    familyId: 'SPR-KLANK-VERDUBBEL',
    defaultExplanation: 'Bij een korte klank in een open lettergreep verdubbel je de medeklinker: bakken → bak-ken, rennen → ren-nen.',
    requiresHints: true,
    requiresVisual: false,
    hints: [{
      title: 'Wanneer verdubbelen?',
      content: 'Korte klank + meervoud/verbuiging?\nVerdubbel de medeklinker:\nbak → bakken\nren → rennen\npot → potten\nbed → bedden'
    }, {
      title: 'Waarom?',
      content: 'Zonder verdubbeling wordt de klank lang:\nbaken (lang) ≠ bakken (kort)\nrenen (lang) ≠ rennen (kort)'
    }]
  },
  'SPR-KLANK-VERKORTEN': {
    familyId: 'SPR-KLANK-VERKORTEN',
    defaultExplanation: 'Bij een lange klank in een gesloten lettergreep schrijf je twee klinkers: boom → bomen (één o in open lettergreep).',
    requiresHints: true,
    requiresVisual: false,
    hints: [{
      title: 'Regel',
      content: 'Lange klank in gesloten lettergreep = dubbele klinker:\nboom, maan, been, boot\n\nIn open lettergreep = enkele klinker:\nbo-men, ma-nen, be-nen, bo-ten'
    }, {
      title: 'Tip',
      content: 'Splits het woord in lettergrepen:\nboom → bo-men (open → één o)\nmaan → ma-nen (open → één a)'
    }]
  },
  'SPR-KLANK-AA-EE-OO': {
    familyId: 'SPR-KLANK-AA-EE-OO',
    defaultExplanation: 'Lange klinkers (aa, ee, oo, uu) schrijf je dubbel in een gesloten lettergreep en enkel in een open lettergreep.',
    requiresHints: true,
    requiresVisual: false,
    hints: [{
      title: 'Gesloten = dubbel',
      content: 'Gesloten lettergreep (eindigt op medeklinker):\nmaan, been, boom, vuur\n\nOpen lettergreep (eindigt op klinker):\nma-nen, be-nen, bo-men'
    }, {
      title: 'Ezelsbruggetje',
      content: 'Gesloten deur = dubbele klinker (aa, ee, oo)\nOpen deur = enkele klinker (a, e, o)'
    }]
  },
  'SPR-KLANK-AU-OU': {
    familyId: 'SPR-KLANK-AU-OU',
    defaultExplanation: 'Au en ou klinken hetzelfde maar worden anders geschreven. De meeste woorden schrijf je met ou. Au komt voor in: blauw, gauw, nauw, rauw.',
    requiresHints: true,
    requiresVisual: false,
    hints: [{
      title: 'Woorden met au',
      content: 'Veel voorkomende au-woorden:\nblauw, gauw, nauw, rauw, pauw\nauteur, auto, pauze, saus'
    }, {
      title: 'Tip',
      content: 'De meeste woorden hebben ou:\noud, koud, goud, hout, zout, mout\n\nAu-woorden moet je uit je hoofd leren!'
    }]
  },
  'SPR-WEET-D-T': {
    familyId: 'SPR-WEET-D-T',
    defaultExplanation: 'Sommige woorden eindigen op -d maar klinken als -t: hond, hand, mond. Maak het woord langer om te horen: honden, handen, monden.',
    requiresHints: true,
    requiresVisual: false,
    hints: [{
      title: 'Verleng het woord',
      content: 'Twijfel je tussen d of t?\nMaak het woord langer:\nhond → honden (d!)\nhand → handen (d!)\nkat → katten (t!)'
    }, {
      title: 'Veelgemaakte fouten',
      content: 'hont → FOUT (honden → d)\nkad → FOUT (katten → t)\nmond → goed (monden → d)'
    }]
  },
  'SPR-WEET-DT': {
    familyId: 'SPR-WEET-DT',
    defaultExplanation: 'Dt-woorden: wordt, vindt, houdt. De stam eindigt op -d, en bij hij/zij komt er -t bij: d + t = dt.',
    requiresHints: true,
    requiresVisual: false,
    hints: [{
      title: 'Regel',
      content: 'Stam op -d + hij/zij = dt:\nhij vindt (stam = vind + t)\nhij wordt (stam = word + t)\nhij houdt (stam = houd + t)'
    }, {
      title: 'Ik-vorm: geen t!',
      content: 'Bij ik alleen de stam:\nik vind (niet: ik vindt)\nik word (niet: ik wordt)\nik houd (niet: ik houdt)'
    }]
  },
  'SPR-LEEN-GRIEKS': {
    familyId: 'SPR-LEEN-GRIEKS',
    defaultExplanation: 'Griekse leenwoorden hebben bijzondere spellingen: th (theater), ph (filosofie), ch (chaos), y (systeem).',
    requiresHints: true,
    requiresVisual: false,
    hints: [{
      title: 'Griekse klankgroepen',
      content: 'th = t-klank: theater, therapie, thermometer\nph = f-klank: filosofie, fotografie\nch = k-klank: chaos, choreografie\ny = ie-klank: systeem, gymnasium'
    }, {
      title: 'Meervouden',
      content: 'Griekse meervouden:\ncrisis → crises\nanalyse → analyses\nhypothese → hypotheses'
    }]
  },
  'SPR-SAM-TUSSEN-S': {
    familyId: 'SPR-SAM-TUSSEN-S',
    defaultExplanation: 'Bij samenstellingen komt er een tussen-s als het eerste deel een bezitsrelatie uitdrukt: dorpsplein, koningsdag, stationshal.',
    requiresHints: true,
    requiresVisual: false,
    hints: [{
      title: 'Wanneer tussen-s?',
      content: 'Het eerste deel "bezit" het tweede:\ndorpsplein (plein van het dorp)\nkoningsdag (dag van de koning)\nstationshal (hal van het station)'
    }, {
      title: 'Geen tussen-s',
      content: 'Als het eerste deel geen bezit uitdrukt:\nboekenkast (kast voor boeken)\npannenkoek (koek van pannen)'
    }]
  },
  'SPR-HOOFDLETTER': {
    familyId: 'SPR-HOOFDLETTER',
    defaultExplanation: 'Hoofdletters gebruik je aan het begin van een zin, bij namen, plaatsen, landen, en bij de aanhef van een brief.',
    requiresHints: true,
    requiresVisual: false,
    hints: [{
      title: 'Wanneer hoofdletter?',
      content: 'Begin van een zin: De kat zit op de mat.\nNamen: Jan, Amsterdam, Nederland\nTitels: Meneer De Vries\nAanhef: Beste mevrouw,'
    }, {
      title: 'Geen hoofdletter',
      content: 'Seizoenen: lente, zomer (geen hoofdletter)\nMaanden en dagen: WEL hoofdletter in het Engels, NIET in het Nederlands\nmaandag, januari (geen hoofdletter)'
    }]
  },
  'SPR-LEESTEKEN-PUNT-KOMMA': {
    familyId: 'SPR-LEESTEKEN-PUNT-KOMMA',
    defaultExplanation: 'Een punt sluit een zin af. Een komma scheidt zinsdelen of items in een opsomming.',
    requiresHints: true,
    requiresVisual: false,
    hints: [{
      title: 'Punt',
      content: 'Aan het eind van een zin: De kat zit op de mat.\nBij afkortingen: bijv., enz., dhr.'
    }, {
      title: 'Komma',
      content: 'Bij opsommingen: appels, peren en bananen\nVoor voegwoorden: Hij rent, maar hij is moe.\nBij bijzinnen: Als het regent, blijf ik thuis.'
    }]
  }
};

/**
 * Module Statistics
 * Auto-calculated from support entries
 */
export const SPR_SUPPORT_STATS = {
  moduleCode: 'SPR',
  moduleName: 'Spelling Regels',
  totalFamilies: Object.keys(SPR_SUPPORT).length,
  withHints: Object.values(SPR_SUPPORT).filter(e => e.hints?.length).length,
  withVisuals: Object.values(SPR_SUPPORT).filter(e => e.defaultVisual).length,
  withExplanations: Object.values(SPR_SUPPORT).filter(e => e.defaultExplanation).length,
  requiresHintsCount: Object.values(SPR_SUPPORT).filter(e => e.requiresHints).length,
  requiresVisualCount: Object.values(SPR_SUPPORT).filter(e => e.requiresVisual).length
};